import type { NamedAppliedModification, TaskConflict } from 'src/services/jmr/types';
import type { TableDataRow } from 'src/utils/jmrUtils';

import type { CheckboxState, FreeFormTextState } from './applyModificationsModal';

export const getModificationKey = (taskId: number, modificationId: number) => `${taskId}-${modificationId}`;

export const hasSelectedModification = (conflict: TaskConflict, checkboxState: CheckboxState) =>
  conflict.availableModifications.some(
    (modification) => checkboxState[getModificationKey(conflict.taskId, modification.id)],
  );

export const isRequiredModificationInfoComplete = (
  conflict: TaskConflict,
  checkboxState: CheckboxState,
  freeFormTextState: FreeFormTextState,
) =>
  conflict.availableModifications?.every(
    (modification) =>
      !checkboxState[getModificationKey(conflict.taskId, modification.id)] ||
      !modification.isFreeFormTextAllowed ||
      freeFormTextState[getModificationKey(conflict.taskId, modification.id)]?.length > 0,
  );

export const areModificationsValid = (
  jobToModify: TableDataRow | null,
  checkboxState: CheckboxState,
  freeFormTextState: FreeFormTextState,
) =>
  jobToModify?.conflicts.every(
    (conflict) =>
      isRequiredModificationInfoComplete(conflict, checkboxState, freeFormTextState) &&
      (+conflict.restrictedValue > 0 || hasSelectedModification(conflict, checkboxState)),
  );

export const buildConflictsWithAppliedModifications = (
  conflicts: TaskConflict[],
  checkboxState: CheckboxState,
  freeFormTextState: FreeFormTextState,
): TaskConflict[] =>
  conflicts.map((conflict) => ({
    ...conflict,
    appliedModifications: conflict.availableModifications
      .filter((modification) => checkboxState[getModificationKey(conflict.taskId, modification.id)])
      .map<NamedAppliedModification>((m) => ({
        taskId: conflict.taskId,
        modificationId: m.id,
        name: m.name,
        freeFormText: freeFormTextState[getModificationKey(conflict.taskId, m.id)],
      })),
  }));

export const calculateMaxAssociateHours = (modifiedConflicts: TaskConflict[], shiftLength: number) =>
  modifiedConflicts.reduce(
    (acc, c) =>
      c.appliedModifications?.length ? acc : Math.min(Math.floor(+c.restrictedValue / +c.requirementProportion), acc),
    shiftLength,
  );

export const applyModificationsToShift = (
  row: TableDataRow,
  shiftId: string,
  modifiedConflicts: TaskConflict[],
  isChecked: boolean,
  shiftLength: number,
) => {
  const maximumHoursAssociateCanDo = calculateMaxAssociateHours(modifiedConflicts, shiftLength);
  const newShifts = row.shifts.map((s) =>
    s.id === shiftId
      ? {
          ...s,
          isChecked,
          totalHours: !isChecked ? '0' : s.totalHours,
          conflicts: modifiedConflicts,
          maximumHoursAssociateCanDo,
        }
      : s,
  );
  const anyChecked = newShifts.some((s) => s.isChecked);
  return {
    ...row,
    isChecked: anyChecked,
    totalHours: newShifts.reduce((sum, s) => (s.isChecked ? sum + (+s.totalHours || 0) : sum), 0).toString(),
    shifts: newShifts,
  };
};

export const applyModificationsToRow = (
  row: TableDataRow,
  modifiedConflicts: TaskConflict[],
  isChecked: boolean,
  shiftLength: number,
) => ({
  ...row,
  conflicts: modifiedConflicts,
  isChecked,
  totalHours: !isChecked ? '0' : row.totalHours,
  maximumHoursAssociateCanDo: calculateMaxAssociateHours(modifiedConflicts, shiftLength),
});
